import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { clientSettingsAPI, shiftAPI, userAPI, branchAPI } from '../../../services';
import { getPTStateOptions, getPTSlabs, PT_STATES, FREQUENCY_LABELS } from '../../../utils/professionalTax';
import { useAsyncAction } from '../../../hooks';
import { DollarSign, Users } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
  Modal,
  Badge,
} from '../../UI';
import { 
  Settings, 
  Clock, 
  Building2, 
  Save, 
  Upload, 
  MapPin, 
  Plus, 
  Edit, 
  Trash2, 
  Image,
  Phone,
  Mail,
  FileText,
  Globe,
  Navigation,
  AlertTriangle,
  CheckCircle,
  X,
  Wallet,
  Percent,
  Calendar,
  RefreshCw,
  Briefcase,
  Tag,
  Lock,
  GitBranch,
  Bell
} from 'lucide-react';

const INDUSTRIES = [
  'IT/Software', 'Manufacturing', 'Retail', 'Healthcare', 'Education',
  'Finance/Banking', 'Hospitality', 'Construction', 'Agriculture',
  'Logistics', 'Real Estate', 'Telecom', 'E-commerce', 'Consulting',
  'Legal', 'Media/Entertainment', 'Other'
];

const DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'];

const AdminSettingsPage = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const signatureInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('organization');
  const [payrollSignature, setPayrollSignature] = useState(null);
  
  const { loading: saveLoading, execute: executeSave } = useAsyncAction();
  const { loading: logoLoading, execute: executeLogo } = useAsyncAction();
  const { loading: signatureLoading, execute: executeSignature } = useAsyncAction();
  const { loading: locationLoading, execute: executeLocation } = useAsyncAction();
  
  // Office Location Modal
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius: 200
  });
  
  // Departments & Designations state
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]); // For HOD dropdown
  const [newDepartment, setNewDepartment] = useState('');
  const [newDesignation, setNewDesignation] = useState('');
  const [newDesignationRank, setNewDesignationRank] = useState('');
  const [selectedDeptForDesig, setSelectedDeptForDesig] = useState(''); // For adding new designation
  const [editingDept, setEditingDept] = useState(null);
  const [editingDesig, setEditingDesig] = useState(null);
  const [editDeptName, setEditDeptName] = useState('');
  const [editDesigName, setEditDesigName] = useState('');
  const [editDesigDept, setEditDesigDept] = useState(''); // For editing designation
  const [editDesigRank, setEditDesigRank] = useState('');

  // Branches state (Approval Chain Engine)
  const [branches, setBranches] = useState([]);
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchCode, setNewBranchCode] = useState('');
  const [editingBranch, setEditingBranch] = useState(null);
  const [editBranchName, setEditBranchName] = useState('');
  const [editBranchCode, setEditBranchCode] = useState('');
  const [branchLoading, setBranchLoading] = useState(false);
  const [deptDesigLoading, setDeptDesigLoading] = useState(false);
  
  // Department Modal state
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({
    name: '',
    hod: '',
    canRaiseJobRequest: false,
    branchWise: false,
    escalateToHR: true,
    isHR: false
  });

  // Shift Management state
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    name: '',
    startTime: '09:00',
    endTime: '18:00',
    gracePeriodMinutes: 15,
    minimumWorkHours: 8,
    workingDays: [1, 2, 3, 4, 5],
    isNightShift: false,
    isDefault: false,
  });

  const [settings, setSettings] = useState({
    name: '',
    phone: '',
    email: '',
    industry: 'Other',
    dateFormat: 'DD/MM/YYYY',
    fieldSeparator: '/',
    gstin: '',
    pan: '',
    tan: '',
    logo: null,
    address: {
      street: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
    },
    filingAddress: {
      name: 'Head Office',
      street: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
      useAsOrganizationAddress: true,
    },
    contactInfo: {
      primaryContactName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      hrEmail: '',
      financeEmail: '',
      notificationEmail: '',
    },
    workTimings: {
      startTime: '09:00',
      endTime: '18:00',
      gracePeriodMinutes: 15,
      minimumWorkHours: 8,
      workingDays: [1, 2, 3, 4, 5], // Mon-Fri (0=Sun, 6=Sat)
      trackingIntervalHours: 1, // Location check interval in hours
    },
    officeLocations: [],
    geofenceAlerts: {
      enabled: true,
      notifyUsers: [],
      channels: { email: false, app: true },
    },
    payrollSettings: {
      payPeriodStartDay: 1,
      payPeriodEndDay: 1,
      payCycleLocked: false,
      payCycleLockedAt: null,
      basicSalaryPercentage: 50,
      hraPercentage: 40,
      salaryComponents: [],
      policies: {
        enableSandwichRule: false,
        enableLateMarkDeduction: true,
        enableHalfDaySplit: true,
        enableCompOffAdjustment: true,
        enableOvertime: true,
        holidayWork: { compensation: 'none', payMultiplier: 2, compOffDays: 1 }
      },
      professionalTax: {
        enabled: true,
        defaultState: '',
        overrideEmployeeState: false
      }
    },
    leaveSettings: {
      leaveTypes: {
        sick: { enabled: true, annualEntitlement: 12, accrualRate: 1, carryForward: false, maxCarryForward: 0, encashable: false },
        casual: { enabled: true, annualEntitlement: 12, accrualRate: 1, carryForward: false, maxCarryForward: 0, encashable: false },
        earned: { enabled: true, annualEntitlement: 15, accrualRate: 1.25, carryForward: true, maxCarryForward: 30, encashable: true },
        compensatory: { enabled: true, annualEntitlement: 0, carryForward: true, maxCarryForward: 10, encashable: false },
        unpaid: { enabled: true, annualEntitlement: 0 }
      },
      accrualMethod: 'monthly',
      accrualDay: 1,
      autoCredit: { enabled: true, creditOnJoining: true, creditAtStartOfPeriod: true },
      carryForward: { enabled: true, maxDays: 30, expiryMonths: 3, processMonth: 4 },
      probationRestriction: { enabled: true, restrictedLeaveTypes: ['earned'], probationMonths: 6 },
      minServiceForLeave: 0,
      requireApproval: true,
      notifyOnApplication: true,
      notifyOnApproval: true,
      allowCrossYearLeave: true
    },
    employeeIdSettings: {
      enabled: true,
      prefix: 'EMP',
      separator: '',
      nextNumber: 1,
      padding: 3
    },
    editPermissions: {
      canEditEmployeeSalary: false,
      canEditPayrollDates: false
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await clientSettingsAPI.getMySettings();
      if (response.status === 'success' && response.data) {
        const data = response.data;
        setSettings({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          industry: data.industry || 'Other',
          dateFormat: data.dateFormat || 'DD/MM/YYYY',
          fieldSeparator: data.fieldSeparator || '/',
          gstin: data.gstin || '',
          pan: data.pan || '',
          tan: data.tan || '',
          logo: data.logo || null,
          address: {
            street: data.address?.street || '',
            city: data.address?.city || '',
            state: data.address?.state || '',
            country: data.address?.country || 'India',
            pincode: data.address?.pincode || '',
          },
          filingAddress: {
            name: data.filingAddress?.name || 'Head Office',
            street: data.filingAddress?.street || '',
            city: data.filingAddress?.city || '',
            state: data.filingAddress?.state || '',
            country: data.filingAddress?.country || 'India',
            pincode: data.filingAddress?.pincode || '',
            useAsOrganizationAddress: data.filingAddress?.useAsOrganizationAddress ?? true,
          },
          contactInfo: {
            primaryContactName: data.contactInfo?.primaryContactName || '',
            primaryContactEmail: data.contactInfo?.primaryContactEmail || '',
            primaryContactPhone: data.contactInfo?.primaryContactPhone || '',
            hrEmail: data.contactInfo?.hrEmail || '',
            financeEmail: data.contactInfo?.financeEmail || '',
            notificationEmail: data.contactInfo?.notificationEmail || '',
          },
          workTimings: {
            startTime: data.workTimings?.startTime || '09:00',
            endTime: data.workTimings?.endTime || '18:00',
            gracePeriodMinutes: data.workTimings?.gracePeriodMinutes || 15,
            minimumWorkHours: data.workTimings?.minimumWorkHours || 8,
            workingDays: data.workTimings?.workingDays || [1, 2, 3, 4, 5],
            trackingIntervalHours: data.workTimings?.trackingIntervalHours || 1,
          },
          officeLocations: data.officeLocations || [],
          geofenceAlerts: {
            enabled: data.geofenceAlerts?.enabled ?? true,
            notifyUsers: (data.geofenceAlerts?.notifyUsers || []).map(u => (typeof u === 'string' ? u : u?._id)).filter(Boolean),
            channels: {
              email: data.geofenceAlerts?.channels?.email ?? false,
              app: data.geofenceAlerts?.channels?.app ?? true,
            },
          },
          payrollSettings: {
            payPeriodStartDay: data.payrollSettings?.payPeriodStartDay ?? 1,
            payPeriodEndDay: data.payrollSettings?.payPeriodEndDay ?? 1,
            payCycleLocked: data.payrollSettings?.payCycleLocked ?? false,
            payCycleLockedAt: data.payrollSettings?.payCycleLockedAt || null,
            basicSalaryPercentage: data.payrollSettings?.basicSalaryPercentage ?? 50,
            hraPercentage: data.payrollSettings?.hraPercentage ?? 40,
            salaryComponents: data.payrollSettings?.salaryComponents || [],
            policies: {
              enableSandwichRule: data.payrollSettings?.policies?.enableSandwichRule ?? false,
              enableLateMarkDeduction: data.payrollSettings?.policies?.enableLateMarkDeduction ?? true,
              enableHalfDaySplit: data.payrollSettings?.policies?.enableHalfDaySplit ?? true,
              enableCompOffAdjustment: data.payrollSettings?.policies?.enableCompOffAdjustment ?? true,
              enableOvertime: data.payrollSettings?.policies?.enableOvertime ?? true,
              holidayWork: {
                compensation: data.payrollSettings?.policies?.holidayWork?.compensation || 'none',
                payMultiplier: data.payrollSettings?.policies?.holidayWork?.payMultiplier ?? 2,
                compOffDays: data.payrollSettings?.policies?.holidayWork?.compOffDays ?? 1
              }
            },
            professionalTax: {
              enabled: data.payrollSettings?.professionalTax?.enabled ?? true,
              defaultState: data.payrollSettings?.professionalTax?.defaultState || '',
              overrideEmployeeState: data.payrollSettings?.professionalTax?.overrideEmployeeState ?? false
            }
          },
          leaveSettings: {
            leaveTypes: {
              sick: { 
                enabled: data.leaveSettings?.leaveTypes?.sick?.enabled ?? true, 
                annualEntitlement: data.leaveSettings?.leaveTypes?.sick?.annualEntitlement ?? 12, 
                accrualRate: data.leaveSettings?.leaveTypes?.sick?.accrualRate ?? 1, 
                carryForward: data.leaveSettings?.leaveTypes?.sick?.carryForward ?? false, 
                maxCarryForward: data.leaveSettings?.leaveTypes?.sick?.maxCarryForward ?? 0, 
                encashable: data.leaveSettings?.leaveTypes?.sick?.encashable ?? false 
              },
              casual: { 
                enabled: data.leaveSettings?.leaveTypes?.casual?.enabled ?? true, 
                annualEntitlement: data.leaveSettings?.leaveTypes?.casual?.annualEntitlement ?? 12, 
                accrualRate: data.leaveSettings?.leaveTypes?.casual?.accrualRate ?? 1, 
                carryForward: data.leaveSettings?.leaveTypes?.casual?.carryForward ?? false, 
                maxCarryForward: data.leaveSettings?.leaveTypes?.casual?.maxCarryForward ?? 0, 
                encashable: data.leaveSettings?.leaveTypes?.casual?.encashable ?? false 
              },
              earned: { 
                enabled: data.leaveSettings?.leaveTypes?.earned?.enabled ?? true, 
                annualEntitlement: data.leaveSettings?.leaveTypes?.earned?.annualEntitlement ?? 15, 
                accrualRate: data.leaveSettings?.leaveTypes?.earned?.accrualRate ?? 1.25, 
                carryForward: data.leaveSettings?.leaveTypes?.earned?.carryForward ?? true, 
                maxCarryForward: data.leaveSettings?.leaveTypes?.earned?.maxCarryForward ?? 30, 
                encashable: data.leaveSettings?.leaveTypes?.earned?.encashable ?? true 
              },
              compensatory: { 
                enabled: data.leaveSettings?.leaveTypes?.compensatory?.enabled ?? true, 
                annualEntitlement: data.leaveSettings?.leaveTypes?.compensatory?.annualEntitlement ?? 0, 
                carryForward: data.leaveSettings?.leaveTypes?.compensatory?.carryForward ?? true, 
                maxCarryForward: data.leaveSettings?.leaveTypes?.compensatory?.maxCarryForward ?? 10, 
                encashable: data.leaveSettings?.leaveTypes?.compensatory?.encashable ?? false 
              },
              unpaid: { 
                enabled: data.leaveSettings?.leaveTypes?.unpaid?.enabled ?? true, 
                annualEntitlement: data.leaveSettings?.leaveTypes?.unpaid?.annualEntitlement ?? 0 
              }
            },
            accrualMethod: data.leaveSettings?.accrualMethod || 'monthly',
            accrualDay: data.leaveSettings?.accrualDay ?? 1,
            autoCredit: { 
              enabled: data.leaveSettings?.autoCredit?.enabled ?? true, 
              creditOnJoining: data.leaveSettings?.autoCredit?.creditOnJoining ?? true, 
              creditAtStartOfPeriod: data.leaveSettings?.autoCredit?.creditAtStartOfPeriod ?? true 
            },
            carryForward: { 
              enabled: data.leaveSettings?.carryForward?.enabled ?? true, 
              maxDays: data.leaveSettings?.carryForward?.maxDays ?? 30, 
              expiryMonths: data.leaveSettings?.carryForward?.expiryMonths ?? 3, 
              processMonth: data.leaveSettings?.carryForward?.processMonth ?? 4 
            },
            probationRestriction: { 
              enabled: data.leaveSettings?.probationRestriction?.enabled ?? true, 
              restrictedLeaveTypes: data.leaveSettings?.probationRestriction?.restrictedLeaveTypes || ['earned'], 
              probationMonths: data.leaveSettings?.probationRestriction?.probationMonths ?? 6 
            },
            minServiceForLeave: data.leaveSettings?.minServiceForLeave ?? 0,
            requireApproval: data.leaveSettings?.requireApproval ?? true,
            notifyOnApplication: data.leaveSettings?.notifyOnApplication ?? true,
            notifyOnApproval: data.leaveSettings?.notifyOnApproval ?? true,
            allowCrossYearLeave: data.leaveSettings?.allowCrossYearLeave ?? true
          },
          employeeIdSettings: {
            enabled: data.employeeIdSettings?.enabled ?? true,
            prefix: data.employeeIdSettings?.prefix || 'EMP',
            separator: data.employeeIdSettings?.separator || '',
            nextNumber: data.employeeIdSettings?.nextNumber ?? 1,
            padding: data.employeeIdSettings?.padding ?? 3
          },
          editPermissions: {
            canEditEmployeeSalary: data.editPermissions?.canEditEmployeeSalary === true,
            canEditPayrollDates: data.editPermissions?.canEditPayrollDates === true
          },
        });
        // Load payroll signature separately
        setPayrollSignature(data.payrollSignature || null);
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments & designations
  const fetchDepartments = async () => {
    try {
      const res = await clientSettingsAPI.getDepartments();
      if (res.status === 'success') setDepartments(res.data || []);
    } catch (e) { console.error('Failed to fetch departments', e); }
  };
  const fetchDesignations = async () => {
    try {
      const res = await clientSettingsAPI.getDesignations();
      if (res.status === 'success') setDesignations(res.data || []);
    } catch (e) { console.error('Failed to fetch designations', e); }
  };
  const fetchEmployees = async () => {
    try {
      const res = await userAPI.getUsers({ limit: 1000, active: true });
      if (res.status === 'success') {
        // Filter active employees
        const activeEmployees = (res.data?.users || res.data || []).filter(u => u.active !== false);
        setEmployees(activeEmployees);
      }
    } catch (e) { console.error('Failed to fetch employees', e); }
  };
  const fetchBranches = async () => {
    try {
      const res = await branchAPI.getBranches();
      if (res.status === 'success') setBranches(res.data || []);
    } catch (e) { console.error('Failed to fetch branches', e); }
  };

  useEffect(() => {
    fetchDepartments();
    fetchDesignations();
    fetchEmployees();
    fetchBranches();
  }, []);

  // Department CRUD
  const openDeptModal = (dept = null) => {
    if (dept) {
      setEditingDept(dept._id);
      setDeptForm({
        name: dept.name || '',
        hod: dept.hod?._id || dept.hod || '',
        canRaiseJobRequest: dept.canRaiseJobRequest || false,
        branchWise: dept.branchWise || false,
        escalateToHR: dept.escalateToHR !== false,
        isHR: dept.isHR || false
      });
    } else {
      setEditingDept(null);
      setDeptForm({ name: '', hod: '', canRaiseJobRequest: false, branchWise: false, escalateToHR: true, isHR: false });
    }
    setIsDeptModalOpen(true);
  };

  const handleSaveDepartment = async () => {
    if (!deptForm.name.trim()) {
      setError('Department name is required');
      return;
    }
    setDeptDesigLoading(true);
    try {
      let res;
      const payload = {
        name: deptForm.name.trim(),
        hod: deptForm.hod || undefined,
        canRaiseJobRequest: deptForm.canRaiseJobRequest,
        branchWise: deptForm.branchWise,
        escalateToHR: deptForm.escalateToHR,
        isHR: deptForm.isHR
      };
      if (editingDept) {
        res = await clientSettingsAPI.updateDepartment(editingDept, payload);
      } else {
        res = await clientSettingsAPI.addDepartment(payload);
      }
      if (res.status === 'success') {
        setDepartments(res.data || []);
        setIsDeptModalOpen(false);
        setEditingDept(null);
        setDeptForm({ name: '', hod: '', canRaiseJobRequest: false, branchWise: false, escalateToHR: true, isHR: false });
        setSuccess(editingDept ? 'Department updated' : 'Department added');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to save department');
      }
    } catch (e) { setError(e.message || 'Failed to save department'); }
    setDeptDesigLoading(false);
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) return;
    setDeptDesigLoading(true);
    try {
      const res = await clientSettingsAPI.addDepartment({ name: newDepartment.trim() });
      if (res.status === 'success') {
        setDepartments(res.data || []);
        setNewDepartment('');
        setSuccess('Department added');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to add department');
      }
    } catch (e) { setError(e.message || 'Failed to add department'); }
    setDeptDesigLoading(false);
  };
  const handleUpdateDepartment = async (id) => {
    if (!editDeptName.trim()) return;
    setDeptDesigLoading(true);
    try {
      const dept = departments.find(d => d._id === id);
      const res = await clientSettingsAPI.updateDepartment(id, { 
        name: editDeptName.trim(),
        hod: dept?.hod?._id || dept?.hod,
        canRaiseJobRequest: dept?.canRaiseJobRequest
      });
      if (res.status === 'success') {
        setDepartments(res.data || []);
        setEditingDept(null);
        setEditDeptName('');
        setSuccess('Department updated');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to update department');
      }
    } catch (e) { setError(e.message || 'Failed to update department'); }
    setDeptDesigLoading(false);
  };
  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Delete this department?')) return;
    setDeptDesigLoading(true);
    try {
      const res = await clientSettingsAPI.deleteDepartment(id);
      if (res.status === 'success') {
        setDepartments(res.data || []);
        setSuccess('Department deleted');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError(e.message || 'Failed to delete department'); }
    setDeptDesigLoading(false);
  };

  // Designation CRUD
  const handleAddDesignation = async () => {
    if (!newDesignation.trim()) return;
    if (!selectedDeptForDesig) {
      setError('Please select a department for the designation');
      return;
    }
    setDeptDesigLoading(true);
    try {
      const res = await clientSettingsAPI.addDesignation(newDesignation.trim(), selectedDeptForDesig, newDesignationRank === '' ? 0 : Number(newDesignationRank));
      if (res.status === 'success') {
        setDesignations(res.data || []);
        setNewDesignation('');
        setNewDesignationRank('');
        setSuccess('Designation added');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to add designation');
      }
    } catch (e) { setError(e.message || 'Failed to add designation'); }
    setDeptDesigLoading(false);
  };
  const handleUpdateDesignation = async (id) => {
    if (!editDesigName.trim()) return;
    setDeptDesigLoading(true);
    try {
      const res = await clientSettingsAPI.updateDesignation(id, editDesigName.trim(), editDesigDept || undefined, editDesigRank === '' ? 0 : Number(editDesigRank));
      if (res.status === 'success') {
        setDesignations(res.data || []);
        setEditingDesig(null);
        setEditDesigName('');
        setEditDesigDept('');
        setEditDesigRank('');
        setSuccess('Designation updated');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to update designation');
      }
    } catch (e) { setError(e.message || 'Failed to update designation'); }
    setDeptDesigLoading(false);
  };
  const handleDeleteDesignation = async (id) => {
    if (!window.confirm('Delete this designation?')) return;
    setDeptDesigLoading(true);
    try {
      const res = await clientSettingsAPI.deleteDesignation(id);
      if (res.status === 'success') {
        setDesignations(res.data || []);
        setSuccess('Designation deleted');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError(e.message || 'Failed to delete designation'); }
    setDeptDesigLoading(false);
  };

  // Branch CRUD (Approval Chain Engine)
  const handleAddBranch = async () => {
    if (!newBranchName.trim()) return;
    setBranchLoading(true);
    try {
      const res = await branchAPI.addBranch({ name: newBranchName.trim(), code: newBranchCode.trim() });
      if (res.status === 'success') {
        setBranches([...branches, res.data]);
        setNewBranchName('');
        setNewBranchCode('');
        setSuccess('Branch added');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to add branch');
      }
    } catch (e) { setError(e.message || 'Failed to add branch'); }
    setBranchLoading(false);
  };
  const handleUpdateBranch = async (id) => {
    if (!editBranchName.trim()) return;
    setBranchLoading(true);
    try {
      const res = await branchAPI.updateBranch(id, { name: editBranchName.trim(), code: editBranchCode.trim() });
      if (res.status === 'success') {
        setBranches(branches.map(b => (b._id === id ? res.data : b)));
        setEditingBranch(null);
        setEditBranchName('');
        setEditBranchCode('');
        setSuccess('Branch updated');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(res.message || 'Failed to update branch');
      }
    } catch (e) { setError(e.message || 'Failed to update branch'); }
    setBranchLoading(false);
  };
  const handleDeleteBranch = async (id) => {
    if (!window.confirm('Delete this branch?')) return;
    setBranchLoading(true);
    try {
      const res = await branchAPI.deleteBranch(id);
      if (res.status === 'success') {
        setBranches(branches.filter(b => b._id !== id));
        setSuccess('Branch deleted');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError(e.message || 'Failed to delete branch'); }
    setBranchLoading(false);
  };

  // ==================== Shift Management ====================
  const fetchShifts = async () => {
    setShiftsLoading(true);
    try {
      const res = await shiftAPI.getShifts();
      if (res.status === 'success') {
        const data = res.data;
        setShifts(Array.isArray(data) ? data : data?.shifts || []);
      }
    } catch (e) { console.error('Failed to fetch shifts:', e); }
    setShiftsLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'shifts') {
      fetchShifts();
    }
  }, [activeTab]);

  const openShiftModal = (shift = null) => {
    if (shift) {
      setEditingShift(shift);
      setShiftForm({
        name: shift.name || '',
        startTime: shift.startTime || '09:00',
        endTime: shift.endTime || '18:00',
        gracePeriodMinutes: shift.gracePeriodMinutes ?? 15,
        minimumWorkHours: shift.minimumWorkHours ?? 8,
        workingDays: shift.workingDays || [1, 2, 3, 4, 5],
        isNightShift: shift.isNightShift || false,
        isDefault: shift.isDefault || false,
      });
    } else {
      setEditingShift(null);
      setShiftForm({
        name: '',
        startTime: '09:00',
        endTime: '18:00',
        gracePeriodMinutes: 15,
        minimumWorkHours: 8,
        workingDays: [1, 2, 3, 4, 5],
        isNightShift: false,
        isDefault: false,
      });
    }
    setIsShiftModalOpen(true);
  };

  const handleSaveShift = async () => {
    if (!shiftForm.name.trim()) {
      setError('Shift name is required');
      return;
    }
    setShiftsLoading(true);
    try {
      let res;
      if (editingShift) {
        res = await shiftAPI.updateShift(editingShift._id, shiftForm);
      } else {
        res = await shiftAPI.createShift(shiftForm);
      }
      if (res.status === 'success') {
        setSuccess(editingShift ? 'Shift updated successfully' : 'Shift created successfully');
        setTimeout(() => setSuccess(''), 2000);
        setIsShiftModalOpen(false);
        fetchShifts();
      } else {
        setError(res.message || 'Failed to save shift');
      }
    } catch (e) { setError(e.message || 'Failed to save shift'); }
    setShiftsLoading(false);
  };

  const handleDeleteShift = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    setShiftsLoading(true);
    try {
      const res = await shiftAPI.deleteShift(id);
      if (res.status === 'success') {
        setSuccess('Shift deleted successfully');
        setTimeout(() => setSuccess(''), 2000);
        fetchShifts();
      } else {
        setError(res.message || 'Failed to delete shift');
      }
    } catch (e) { setError(e.message || 'Failed to delete shift'); }
    setShiftsLoading(false);
  };

  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // ── Professional Tax (global defaults) ─────────────────────────────────────
  const ptStateOptions = useMemo(() => getPTStateOptions(), []);
  const ptSettings = settings.payrollSettings?.professionalTax || {};
  const ptDefaultState = ptSettings.defaultState || '';
  const ptStateConfig = PT_STATES[ptDefaultState] || null;

  const updatePT = (patch) => setSettings((prev) => ({
    ...prev,
    payrollSettings: {
      ...prev.payrollSettings,
      professionalTax: { ...(prev.payrollSettings?.professionalTax || {}), ...patch }
    }
  }));

  // Leave entitlements are granted in whole or half days, so 11.5 must survive
  // the input (parseInt would silently drop it to 11).
  const parseHalfDays = (value) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 2) / 2;
  };

  // A disabled leave type grants nothing, whatever its saved Annual Days -
  // mirrors entitlementFor() in backend/src/utils/leaveAccrual.js.
  const effectiveEntitlement = (type) => {
    const config = settings.leaveSettings?.leaveTypes?.[type];
    return config?.enabled === false ? 0 : (config?.annualEntitlement || 0);
  };

  const formatRupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const formatSlabRange = (from, to) => (to === Infinity
    ? `${formatRupees(from)} and above`
    : `${formatRupees(from)} – ${formatRupees(to)}`);
  const ptBasisLabel = {
    monthly: 'monthly gross',
    half_yearly: 'half-yearly gross (monthly × 6)',
    annual: 'annual gross (monthly × 12)'
  };

  const holidayWork = settings.payrollSettings?.policies?.holidayWork || { compensation: 'none', payMultiplier: 2, compOffDays: 1 };
  const updateHolidayWork = (patch) => setSettings((prev) => ({
    ...prev,
    payrollSettings: {
      ...prev.payrollSettings,
      policies: {
        ...prev.payrollSettings?.policies,
        holidayWork: { ...(prev.payrollSettings?.policies?.holidayWork || {}), ...patch }
      }
    }
  }));

  // Core salary split: Basic and HRA are entered as % of CTC and Conveyance
  // takes whatever is left, so the three always add up to 100%.
  const corePercentages = (() => {
    const basic = Number(settings.payrollSettings?.basicSalaryPercentage ?? 50);
    const hra = Number(settings.payrollSettings?.hraPercentage ?? 40);
    return { basic, hra, conveyance: 100 - basic - hra, isValid: basic + hra <= 100 };
  })();

  const handleSave = async (e) => {
    e.preventDefault();
    if (!corePercentages.isValid) {
      setError('Basic Salary % + HRA % cannot exceed 100% of CTC');
      return;
    }
    const { logo, officeLocations, email, ...dataToSave } = settings;
    const result = await executeSave(clientSettingsAPI.updateMySettings, dataToSave);
    if (result.success) {
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to save settings');
    }
  };

  // Out-of-radius alert settings (Office Locations tab has no global save button)
  const updateGeofenceAlerts = (patch) => {
    setSettings((prev) => ({
      ...prev,
      geofenceAlerts: { ...prev.geofenceAlerts, ...patch },
    }));
  };

  const toggleAlertRecipient = (userId) => {
    if (!userId) return;
    setSettings((prev) => {
      const current = prev.geofenceAlerts.notifyUsers || [];
      return {
        ...prev,
        geofenceAlerts: {
          ...prev.geofenceAlerts,
          notifyUsers: current.includes(userId)
            ? current.filter((id) => id !== userId)
            : [...current, userId],
        },
      };
    });
  };

  const handleSaveGeofenceAlerts = async () => {
    const { enabled, notifyUsers, channels } = settings.geofenceAlerts;
    if (enabled && !channels.email && !channels.app) {
      setError('Select at least one notification channel (email or app)');
      return;
    }
    const result = await executeSave(clientSettingsAPI.updateMySettings, {
      geofenceAlerts: { enabled, notifyUsers, channels },
    });
    if (result.success) {
      setError('');
      setSuccess('Out-of-radius alert settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to save alert settings');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setError('Please upload a JPG or PNG image');
      return;
    }
    if (file.size > 1024 * 1024) {
      setError('Image size must be less than 1MB');
      return;
    }

    const result = await executeLogo(clientSettingsAPI.uploadLogo, file);
    if (result.success) {
      setSettings({ ...settings, logo: result.data.data.logo });
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to upload logo');
    }
  };

  const handleDeleteLogo = async () => {
    const result = await executeLogo(clientSettingsAPI.deleteLogo);
    if (result.success) {
      setSettings({ ...settings, logo: null });
      setSuccess('Logo deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete logo');
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setError('Please upload a JPG or PNG image for the signature');
      return;
    }
    if (file.size > 1024 * 1024) {
      setError('Signature image must be less than 1MB');
      return;
    }
    const result = await executeSignature(clientSettingsAPI.uploadSignature, file);
    if (result.success) {
      setPayrollSignature(result.data?.data?.payrollSignature || result.data?.payrollSignature || null);
      setSuccess('Payroll signature uploaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to upload signature');
    }
    // Reset input so same file can be re-selected
    if (signatureInputRef.current) signatureInputRef.current.value = '';
  };

  const handleDeleteSignature = async () => {
    if (!window.confirm('Remove the payroll signature?')) return;
    const result = await executeSignature(clientSettingsAPI.deleteSignature);
    if (result.success) {
      setPayrollSignature(null);
      setSuccess('Payroll signature removed');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to remove signature');
    }
  };

  // Office Location Handlers
  const getCurrentLocation = () => {
    setGettingLocation(true);
    setLocationError('');
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported. Please enter coordinates manually or use Google Maps.');
      setGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationForm({
          ...locationForm,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        setGettingLocation(false);
        setSuccess('Location captured successfully!');
        setTimeout(() => setSuccess(''), 3000);
      },
      (error) => {
        let errorMsg = 'Failed to get location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += 'Location permission denied. Please enter coordinates manually.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += 'Location unavailable. Please enter coordinates manually.';
            break;
          case error.TIMEOUT:
            errorMsg += 'Request timed out. Please try again or enter coordinates manually.';
            break;
          default:
            errorMsg += 'Please enter coordinates manually.';
        }
        setLocationError(errorMsg);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const openGoogleMaps = () => {
    window.open('https://www.google.com/maps', '_blank');
  };

  const openAddLocationModal = () => {
    setEditingLocation(null);
    setLocationForm({ name: '', address: '', latitude: '', longitude: '', radius: 200 });
    setLocationError('');
    setIsLocationModalOpen(true);
  };

  const openEditLocationModal = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name,
      address: location.address || '',
      latitude: location.coordinates?.latitude?.toString() || '',
      longitude: location.coordinates?.longitude?.toString() || '',
      radius: location.radius || 200
    });
    setLocationError('');
    setIsLocationModalOpen(true);
  };

  const handleSaveLocation = async () => {
    if (!locationForm.name) {
      setLocationError('Location name is required.');
      return;
    }
    if (!locationForm.latitude || !locationForm.longitude) {
      setLocationError('Coordinates are required. Enter latitude and longitude manually or use "Get Current Location".');
      return;
    }
    
    // Validate coordinate format
    const lat = parseFloat(locationForm.latitude);
    const lng = parseFloat(locationForm.longitude);
    if (isNaN(lat) || isNaN(lng)) {
      setLocationError('Invalid coordinates. Please enter valid numbers.');
      return;
    }
    if (lat < -90 || lat > 90) {
      setLocationError('Latitude must be between -90 and 90.');
      return;
    }
    if (lng < -180 || lng > 180) {
      setLocationError('Longitude must be between -180 and 180.');
      return;
    }

    const locationData = {
      name: locationForm.name,
      address: locationForm.address,
      latitude: lat,
      longitude: lng,
      radius: parseInt(locationForm.radius) || 200
    };

    let result;
    if (editingLocation) {
      result = await executeLocation(clientSettingsAPI.updateOfficeLocation, editingLocation._id, locationData);
    } else {
      result = await executeLocation(clientSettingsAPI.addOfficeLocation, locationData);
    }

    if (result.success) {
      setSettings({ ...settings, officeLocations: result.data.data.allLocations });
      setIsLocationModalOpen(false);
      setSuccess(editingLocation ? 'Office location updated' : 'Office location added');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setLocationError(result.error || 'Failed to save location');
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this office location?')) return;
    
    const result = await executeLocation(clientSettingsAPI.deleteOfficeLocation, locationId);
    if (result.success) {
      setSettings({ ...settings, officeLocations: result.data.data.allLocations });
      setSuccess('Office location deleted');
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete location');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'organization', label: 'Organization', icon: Building2 },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'contact', label: 'Contact Info', icon: Phone },
    { id: 'office-locations', label: 'Office Locations', icon: Navigation },
    { id: 'departments', label: 'Dept & Designations', icon: Briefcase },
    { id: 'branches', label: 'Branches', icon: GitBranch },
    { id: 'work-timings', label: 'Work Timings', icon: Clock },
    { id: 'shifts', label: 'Shifts', icon: RefreshCw },
    { id: 'payroll', label: 'Payroll', icon: Wallet },
    { id: 'leaves', label: 'Leave Management', icon: Calendar },
  ];

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      <div>
        <h2 className="text-xl font-bold text-gray-900">Organization Settings</h2>
        <p className="text-gray-500">Manage your organization's profile and settings</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSave} className="p-6">
          {/* Organization Tab */}
          {activeTab === 'organization' && (
            <div className="space-y-6">
              {/* Logo Section */}
              <div className="flex items-start gap-6 pb-6 border-b border-gray-200">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden">
                    {settings.logo?.url ? (
                      <img src={settings.logo.url} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Organisation Logo</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    This logo will be displayed on documents such as Payslip and TDS Worksheet.<br />
                    Preferred Image Size: 240 x 240 pixels. Maximum size: 1MB. Formats: PNG, JPG
                  </p>
                  <div className="flex gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      loading={logoLoading}
                      className="flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Logo
                    </Button>
                    {settings.logo?.url && (
                      <Button
                        type="button"
                        variant="danger"
                        onClick={handleDeleteLogo}
                        loading={logoLoading}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input
                    label="Organisation Name *"
                    value={settings.name}
                    onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                    placeholder="Your registered business name"
                  />
                  <p className="text-xs text-gray-500 mt-1">This will appear in all forms and payslips</p>
                </div>
                <Select
                  label="Industry *"
                  value={settings.industry}
                  onChange={(e) => setSettings({ ...settings, industry: e.target.value })}
                >
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </Select>
                <Input
                  label="Phone"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  placeholder="Contact number"
                />
                <Select
                  label="Date Format"
                  value={settings.dateFormat}
                  onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
                >
                  {DATE_FORMATS.map(fmt => (
                    <option key={fmt} value={fmt}>{fmt}</option>
                  ))}
                </Select>
                <Input
                  label="Email"
                  value={settings.email}
                  disabled
                  className="bg-gray-100"
                />
              </div>

              {/* Tax Information */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Tax Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="GSTIN"
                    value={settings.gstin}
                    onChange={(e) => setSettings({ ...settings, gstin: e.target.value.toUpperCase() })}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                  <Input
                    label="PAN"
                    value={settings.pan}
                    onChange={(e) => setSettings({ ...settings, pan: e.target.value.toUpperCase() })}
                    placeholder="AAAAA0000A"
                    maxLength={10}
                  />
                  <Input
                    label="TAN"
                    value={settings.tan}
                    onChange={(e) => setSettings({ ...settings, tan: e.target.value.toUpperCase() })}
                    placeholder="AAAA00000A"
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Employee ID Settings */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Auto Employee ID</h3>
                <p className="text-sm text-gray-500 mb-4">Configure auto-generation of employee IDs when adding new employees</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="empIdEnabled"
                      checked={settings.employeeIdSettings?.enabled !== false}
                      onChange={(e) => setSettings({
                        ...settings,
                        employeeIdSettings: {
                          ...settings.employeeIdSettings,
                          enabled: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="empIdEnabled" className="ml-2 text-sm text-gray-700">
                      Enable Auto-Generation
                    </label>
                  </div>
                  <Input
                    label="Prefix"
                    // `?? ''` (not `|| 'EMP'`): clearing the field must leave it empty,
                    // otherwise EMP snaps back and typing appends to it (EMPADX).
                    value={settings.employeeIdSettings?.prefix ?? ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      employeeIdSettings: {
                        ...settings.employeeIdSettings,
                        prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                      }
                    })}
                    placeholder="EMP"
                    maxLength={10}
                  />
                  <Select
                    label="Separator"
                    value={settings.employeeIdSettings?.separator || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      employeeIdSettings: {
                        ...settings.employeeIdSettings,
                        separator: e.target.value
                      }
                    })}
                  >
                    <option value="">None</option>
                    <option value="-">Hyphen (-)</option>
                    <option value="_">Underscore (_)</option>
                  </Select>
                  <Input
                    label="Next Number"
                    type="number"
                    value={settings.employeeIdSettings?.nextNumber || 1}
                    onChange={(e) => setSettings({
                      ...settings,
                      employeeIdSettings: {
                        ...settings.employeeIdSettings,
                        nextNumber: parseInt(e.target.value) || 1
                      }
                    })}
                    min={1}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Preview: {settings.employeeIdSettings?.prefix || 'EMP'}{settings.employeeIdSettings?.separator || ''}{String(settings.employeeIdSettings?.nextNumber || 1).padStart(settings.employeeIdSettings?.padding || 3, '0')}
                </p>
              </div>
            </div>
          )}

          {/* Address Tab */}
          {activeTab === 'address' && (
            <div className="space-y-6">
              {/* Organization Address */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Organisation Address</h3>
                <p className="text-sm text-gray-500 mb-4">This will be considered as the address of your primary work location</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Street Address"
                      value={settings.address.street}
                      onChange={(e) => setSettings({
                        ...settings,
                        address: { ...settings.address, street: e.target.value }
                      })}
                    />
                  </div>
                  <Input
                    label="City"
                    value={settings.address.city}
                    onChange={(e) => setSettings({
                      ...settings,
                      address: { ...settings.address, city: e.target.value }
                    })}
                  />
                  <Input
                    label="State"
                    value={settings.address.state}
                    onChange={(e) => setSettings({
                      ...settings,
                      address: { ...settings.address, state: e.target.value }
                    })}
                  />
                  <Input
                    label="Country"
                    value={settings.address.country}
                    onChange={(e) => setSettings({
                      ...settings,
                      address: { ...settings.address, country: e.target.value }
                    })}
                  />
                  <Input
                    label="Pincode"
                    value={settings.address.pincode}
                    onChange={(e) => setSettings({
                      ...settings,
                      address: { ...settings.address, pincode: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* Filing Address */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Filing/Registered Address</h3>
                <p className="text-sm text-gray-500 mb-4">This registered address will be used across all Forms and Payslips</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Address Label"
                    value={settings.filingAddress.name}
                    onChange={(e) => setSettings({
                      ...settings,
                      filingAddress: { ...settings.filingAddress, name: e.target.value }
                    })}
                    placeholder="e.g., Head Office, Registered Office"
                  />
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.filingAddress.useAsOrganizationAddress}
                        onChange={(e) => setSettings({
                          ...settings,
                          filingAddress: { ...settings.filingAddress, useAsOrganizationAddress: e.target.checked }
                        })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Same as organisation address</span>
                    </label>
                  </div>
                  {!settings.filingAddress.useAsOrganizationAddress && (
                    <>
                      <div className="md:col-span-2">
                        <Input
                          label="Street Address"
                          value={settings.filingAddress.street}
                          onChange={(e) => setSettings({
                            ...settings,
                            filingAddress: { ...settings.filingAddress, street: e.target.value }
                          })}
                        />
                      </div>
                      <Input
                        label="City"
                        value={settings.filingAddress.city}
                        onChange={(e) => setSettings({
                          ...settings,
                          filingAddress: { ...settings.filingAddress, city: e.target.value }
                        })}
                      />
                      <Input
                        label="State"
                        value={settings.filingAddress.state}
                        onChange={(e) => setSettings({
                          ...settings,
                          filingAddress: { ...settings.filingAddress, state: e.target.value }
                        })}
                      />
                      <Input
                        label="Country"
                        value={settings.filingAddress.country}
                        onChange={(e) => setSettings({
                          ...settings,
                          filingAddress: { ...settings.filingAddress, country: e.target.value }
                        })}
                      />
                      <Input
                        label="Pincode"
                        value={settings.filingAddress.pincode}
                        onChange={(e) => setSettings({
                          ...settings,
                          filingAddress: { ...settings.filingAddress, pincode: e.target.value }
                        })}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Contact Info Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Primary Contact</h3>
                <p className="text-sm text-gray-500 mb-4">This contact receives reminders and email notifications</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Contact Name"
                    value={settings.contactInfo.primaryContactName}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, primaryContactName: e.target.value }
                    })}
                  />
                  <Input
                    label="Contact Email"
                    type="email"
                    value={settings.contactInfo.primaryContactEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, primaryContactEmail: e.target.value }
                    })}
                  />
                  <Input
                    label="Contact Phone"
                    value={settings.contactInfo.primaryContactPhone}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, primaryContactPhone: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Department Emails</h3>
                <p className="text-sm text-gray-500 mb-4">Configure email addresses for different departments</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="HR Email"
                    type="email"
                    value={settings.contactInfo.hrEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, hrEmail: e.target.value }
                    })}
                    placeholder="hr@company.com"
                  />
                  <Input
                    label="Finance Email"
                    type="email"
                    value={settings.contactInfo.financeEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, financeEmail: e.target.value }
                    })}
                    placeholder="finance@company.com"
                  />
                  <Input
                    label="Notification Email"
                    type="email"
                    value={settings.contactInfo.notificationEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      contactInfo: { ...settings.contactInfo, notificationEmail: e.target.value }
                    })}
                    placeholder="notifications@company.com"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Office Locations Tab */}
          {activeTab === 'office-locations' && (
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Office Locations</h3>
                  <p className="text-sm text-gray-500">
                    Add your office locations with GPS coordinates. Employees can only punch in/out when they are within the defined radius.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={openAddLocationModal}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Location
                </Button>
              </div>

              {/* Warning Alert */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Important</p>
                  <p className="text-amber-700">
                    When adding a new office location, you must be physically present at that location. 
                    The system will capture your current GPS coordinates to set the office geofence.
                  </p>
                </div>
              </div>

              {/* Locations List */}
              {settings.officeLocations.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Navigation className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 mb-1">No office locations configured</h3>
                  <p className="text-gray-500 mb-4">Add your first office location to enable attendance geofencing</p>
                  <Button type="button" onClick={openAddLocationModal}>
                    Add Office Location
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {settings.officeLocations.map((loc) => (
                    <div key={loc._id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{loc.name}</h4>
                          <p className="text-sm text-gray-500">{loc.address || 'No address'}</p>
                          <p className="text-xs text-gray-400">
                            Lat: {loc.coordinates?.latitude}, Lng: {loc.coordinates?.longitude} | Radius: {loc.radius}m
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={loc.isActive ? 'success' : 'secondary'}>
                          {loc.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => openEditLocationModal(loc)}
                          className="p-2 rounded-lg hover:bg-gray-100"
                        >
                          <Edit className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteLocation(loc._id)}
                          className="p-2 rounded-lg hover:bg-gray-100"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Out-of-Radius Alerts */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-blue-600" />
                      Out-of-Radius Alerts
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Get notified when an employee is detected outside their office radius during a location check.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settings.geofenceAlerts.enabled}
                      onChange={(e) => updateGeofenceAlerts({ enabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {settings.geofenceAlerts.enabled && (
                  <div className="space-y-5 pt-1 border-t border-gray-100">
                    {/* Channels */}
                    <div className="pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notify via
                      </label>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 rounded"
                            checked={settings.geofenceAlerts.channels.email}
                            onChange={(e) => updateGeofenceAlerts({
                              channels: { ...settings.geofenceAlerts.channels, email: e.target.checked }
                            })}
                          />
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-700">Email</span>
                        </label>
                        <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-blue-600 rounded"
                            checked={settings.geofenceAlerts.channels.app}
                            onChange={(e) => updateGeofenceAlerts({
                              channels: { ...settings.geofenceAlerts.channels, app: e.target.checked }
                            })}
                          />
                          <Bell className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-700">App notification</span>
                        </label>
                      </div>
                    </div>

                    {/* Recipients */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employees to notify
                      </label>
                      <select
                        value=""
                        onChange={(e) => toggleAlertRecipient(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select an employee to add...</option>
                        {employees
                          .filter(emp => !(settings.geofenceAlerts.notifyUsers || []).includes(emp._id))
                          .map(emp => (
                            <option key={emp._id} value={emp._id}>
                              {emp.name} {emp.employeeId ? `(${emp.employeeId})` : ''}
                            </option>
                          ))}
                      </select>

                      {(settings.geofenceAlerts.notifyUsers || []).length === 0 ? (
                        <p className="text-xs text-gray-500 mt-2">
                          No one selected — alerts go to the employee's reporting manager by default.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {settings.geofenceAlerts.notifyUsers.map((userId) => {
                            const emp = employees.find(e => e._id === userId);
                            return (
                              <span
                                key={userId}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-sm"
                              >
                                {emp ? emp.name : 'Unknown employee'}
                                <button
                                  type="button"
                                  onClick={() => toggleAlertRecipient(userId)}
                                  className="hover:text-blue-900"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-4 border-t border-gray-100">
                  <Button
                    type="button"
                    onClick={handleSaveGeofenceAlerts}
                    loading={saveLoading}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Alert Settings
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Departments & Designations Tab */}
          {activeTab === 'departments' && (
            <div className="space-y-8">
              {/* Departments Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-700" />
                    <h3 className="font-semibold text-gray-900">Departments</h3>
                  </div>
                  <Button
                    type="button"
                    onClick={() => openDeptModal()}
                    className="flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add Department
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Manage departments, assign HODs, and configure job request permissions</p>

                {/* Department list */}
                {departments.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <Building2 className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No departments added yet</p>
                    <p className="text-sm text-gray-400">Click "Add Department" to create one</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {departments.filter(d => d.isActive !== false).map((dept) => (
                      <div key={dept._id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-800 font-medium">{dept.name}</span>
                              {dept.canRaiseJobRequest && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                  Can Raise Job Requests
                                </span>
                              )}
                              {dept.isHR && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                                  HR Department
                                </span>
                              )}
                              {dept.branchWise && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                  Branch-wise
                                </span>
                              )}
                              {dept.escalateToHR === false && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                                  No HR fallback
                                </span>
                              )}
                            </div>
                            {dept.hod && (
                              <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                                <Users className="w-3 h-3" />
                                <span>HOD: {dept.hod?.name || 'Unknown'}</span>
                                {dept.hod?.employeeId && <span className="text-gray-400">({dept.hod.employeeId})</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button 
                              type="button" 
                              onClick={() => openDeptModal(dept)} 
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Edit Department"
                            >
                              <Edit className="w-4 h-4 text-blue-600" />
                            </button>
                            <button 
                              type="button" 
                              onClick={() => handleDeleteDepartment(dept._id)} 
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Delete Department"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* Designations Section */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-5 h-5 text-gray-700" />
                  <h3 className="font-semibold text-gray-900">Designations</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">Manage the list of designations/job titles available when creating employees. Each designation belongs to a department.</p>
                
                {/* Add new designation */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <select
                    value={selectedDeptForDesig}
                    onChange={(e) => setSelectedDeptForDesig(e.target.value)}
                    className="sm:w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Department</option>
                    {departments.filter(d => d.isActive !== false).map(dept => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newDesignation}
                    onChange={(e) => setNewDesignation(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDesignation())}
                    placeholder="Enter designation name"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    value={newDesignationRank}
                    onChange={(e) => setNewDesignationRank(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDesignation())}
                    placeholder="Rank"
                    title="Seniority rank - higher number is more senior. Used to build the approval chain."
                    className="sm:w-24 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <Button
                    type="button"
                    onClick={handleAddDesignation}
                    loading={deptDesigLoading}
                    disabled={!selectedDeptForDesig}
                    className="flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>

                {/* Designation list grouped by department */}
                {designations.filter(d => d.isActive !== false).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <Tag className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No designations added yet</p>
                    <p className="text-sm text-gray-400">First add departments, then add designations for each department</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {departments.filter(d => d.isActive !== false).map(dept => {
                      const deptDesignations = designations.filter(d => d.isActive !== false && d.department?.toString() === dept._id.toString());
                      if (deptDesignations.length === 0) return null;
                      return (
                        <div key={dept._id} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                            <span className="text-sm font-semibold text-gray-700">{dept.name}</span>
                            <span className="text-xs text-gray-500 ml-2">({deptDesignations.length} designations)</span>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {deptDesignations.map((desig) => (
                              <div key={desig._id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                                {editingDesig === desig._id ? (
                                  <div className="flex items-center gap-2 flex-1">
                                    <select
                                      value={editDesigDept || desig.department?.toString() || ''}
                                      onChange={(e) => setEditDesigDept(e.target.value)}
                                      className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                    >
                                      {departments.filter(d => d.isActive !== false).map(d => (
                                        <option key={d._id} value={d._id}>{d.name}</option>
                                      ))}
                                    </select>
                                    <input
                                      type="text"
                                      value={editDesigName}
                                      onChange={(e) => setEditDesigName(e.target.value)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateDesignation(desig._id)}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                      autoFocus
                                    />
                                    <input
                                      type="number"
                                      value={editDesigRank}
                                      onChange={(e) => setEditDesigRank(e.target.value)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleUpdateDesignation(desig._id)}
                                      title="Seniority rank - higher number is more senior. Used to build the approval chain."
                                      className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                    <button type="button" onClick={() => handleUpdateDesignation(desig._id)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                      <CheckCircle className="w-5 h-5" />
                                    </button>
                                    <button type="button" onClick={() => { setEditingDesig(null); setEditDesigName(''); setEditDesigDept(''); setEditDesigRank(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                      <X className="w-5 h-5" />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="text-gray-800 font-medium">
                                      {desig.name}
                                      <span className="ml-2 text-xs font-normal text-gray-400">rank {desig.rank ?? 0}</span>
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button type="button" onClick={() => { setEditingDesig(desig._id); setEditDesigName(desig.name); setEditDesigDept(desig.department?.toString() || ''); setEditDesigRank(String(desig.rank ?? 0)); }} className="p-2 rounded-lg hover:bg-gray-100">
                                        <Edit className="w-4 h-4 text-blue-600" />
                                      </button>
                                      <button type="button" onClick={() => handleDeleteDesignation(desig._id)} className="p-2 rounded-lg hover:bg-gray-100">
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {/* Show unassigned designations if any */}
                    {designations.filter(d => d.isActive !== false && !d.department).length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-yellow-50 px-4 py-2 border-b border-gray-200">
                          <span className="text-sm font-semibold text-yellow-700">Unassigned</span>
                          <span className="text-xs text-yellow-600 ml-2">(Not linked to any department)</span>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {designations.filter(d => d.isActive !== false && !d.department).map((desig) => (
                            <div key={desig._id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                              {editingDesig === desig._id ? (
                                <div className="flex items-center gap-2 flex-1">
                                  <select
                                    value={editDesigDept}
                                    onChange={(e) => setEditDesigDept(e.target.value)}
                                    className="w-40 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="">Select Department</option>
                                    {departments.filter(d => d.isActive !== false).map(d => (
                                      <option key={d._id} value={d._id}>{d.name}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    value={editDesigName}
                                    onChange={(e) => setEditDesigName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateDesignation(desig._id)}
                                    className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    autoFocus
                                  />
                                  <input
                                    type="number"
                                    value={editDesigRank}
                                    onChange={(e) => setEditDesigRank(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateDesignation(desig._id)}
                                    title="Seniority rank - higher number is more senior. Used to build the approval chain."
                                    className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  />
                                  <button type="button" onClick={() => handleUpdateDesignation(desig._id)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                    <CheckCircle className="w-5 h-5" />
                                  </button>
                                  <button type="button" onClick={() => { setEditingDesig(null); setEditDesigName(''); setEditDesigDept(''); setEditDesigRank(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <span className="text-gray-800 font-medium">
                                    {desig.name}
                                    <span className="ml-2 text-xs font-normal text-gray-400">rank {desig.rank ?? 0}</span>
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <button type="button" onClick={() => { setEditingDesig(desig._id); setEditDesigName(desig.name); setEditDesigDept(''); setEditDesigRank(String(desig.rank ?? 0)); }} className="p-2 rounded-lg hover:bg-gray-100">
                                      <Edit className="w-4 h-4 text-blue-600" />
                                    </button>
                                    <button type="button" onClick={() => handleDeleteDesignation(desig._id)} className="p-2 rounded-lg hover:bg-gray-100">
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Branches Tab */}
          {activeTab === 'branches' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <GitBranch className="w-5 h-5 text-gray-700" />
                <h3 className="font-semibold text-gray-900">Branches</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Used by branch-wise departments in the approval chain - the same designation ladder repeats once
                per branch, and requests route to the approver in the employee's own branch. Assign an employee's
                branch from their Employee profile.
              </p>

              {/* Add new branch */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  type="text"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBranch())}
                  placeholder="Branch name"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  value={newBranchCode}
                  onChange={(e) => setNewBranchCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBranch())}
                  placeholder="Code (optional)"
                  className="sm:w-40 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Button type="button" onClick={handleAddBranch} loading={branchLoading} className="flex items-center gap-1">
                  <Plus className="w-4 h-4" /> Add
                </Button>
              </div>

              {/* Branch list */}
              {branches.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <GitBranch className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No branches added yet</p>
                  <p className="text-sm text-gray-400">Add one if any department needs a per-branch approval chain</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {branches.map((branch) => (
                    <div key={branch._id} className="bg-white border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50">
                      {editingBranch === branch._id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editBranchName}
                            onChange={(e) => setEditBranchName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateBranch(branch._id)}
                            className="flex-1 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <input
                            type="text"
                            value={editBranchCode}
                            onChange={(e) => setEditBranchCode(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateBranch(branch._id)}
                            placeholder="Code"
                            className="w-32 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button type="button" onClick={() => handleUpdateBranch(branch._id)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button type="button" onClick={() => { setEditingBranch(null); setEditBranchName(''); setEditBranchCode(''); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-gray-800 font-medium">{branch.name}</span>
                            {branch.code && <span className="ml-2 text-xs text-gray-400">({branch.code})</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => { setEditingBranch(branch._id); setEditBranchName(branch.name); setEditBranchCode(branch.code || ''); }}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Edit Branch"
                            >
                              <Edit className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteBranch(branch._id)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="Delete Branch"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Work Timings Tab */}
          {activeTab === 'work-timings' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Work Hours</h3>
                <p className="text-sm text-gray-500 mb-4">Configure attendance and work hours settings</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Start Time"
                  type="time"
                  value={settings.workTimings.startTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    workTimings: { ...settings.workTimings, startTime: e.target.value }
                  })}
                />
                <Input
                  label="End Time"
                  type="time"
                  value={settings.workTimings.endTime}
                  onChange={(e) => setSettings({
                    ...settings,
                    workTimings: { ...settings.workTimings, endTime: e.target.value }
                  })}
                />
                <Input
                  label="Grace Period (minutes)"
                  type="number"
                  value={settings.workTimings.gracePeriodMinutes}
                  onChange={(e) => setSettings({
                    ...settings,
                    workTimings: { ...settings.workTimings, gracePeriodMinutes: parseInt(e.target.value) || 0 }
                  })}
                />
                <Input
                  label="Minimum Work Hours"
                  type="number"
                  value={settings.workTimings.minimumWorkHours}
                  onChange={(e) => setSettings({
                    ...settings,
                    workTimings: { ...settings.workTimings, minimumWorkHours: parseInt(e.target.value) || 0 }
                  })}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location Check Interval
                  </label>
                  <select
                    value={settings.workTimings.trackingIntervalHours}
                    onChange={(e) => setSettings({
                      ...settings,
                      workTimings: { ...settings.workTimings, trackingIntervalHours: parseInt(e.target.value) || 1 }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={1}>Every 1 Hour</option>
                    <option value={2}>Every 2 Hours</option>
                    <option value={3}>Every 3 Hours</option>
                    <option value={4}>Every 4 Hours</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">How often to check employee location during work hours</p>
                </div>
              </div>

              {/* Working Days Selection */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Working Days</h3>
                <p className="text-sm text-gray-500 mb-4">Select the days employees are expected to work. Unselected days will be marked as weekends/off days in the calendar.</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { day: 0, name: 'Sunday', short: 'Sun' },
                    { day: 1, name: 'Monday', short: 'Mon' },
                    { day: 2, name: 'Tuesday', short: 'Tue' },
                    { day: 3, name: 'Wednesday', short: 'Wed' },
                    { day: 4, name: 'Thursday', short: 'Thu' },
                    { day: 5, name: 'Friday', short: 'Fri' },
                    { day: 6, name: 'Saturday', short: 'Sat' },
                  ].map(({ day, name, short }) => {
                    const isSelected = settings.workTimings.workingDays?.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const currentDays = settings.workTimings.workingDays || [1, 2, 3, 4, 5];
                          const newDays = isSelected
                            ? currentDays.filter(d => d !== day)
                            : [...currentDays, day].sort((a, b) => a - b);
                          setSettings({
                            ...settings,
                            workTimings: { ...settings.workTimings, workingDays: newDays }
                          });
                        }}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          isSelected
                            ? 'bg-indigo-600 text-white shadow-md hover:bg-indigo-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={name}
                      >
                        <span className="hidden sm:inline">{name}</span>
                        <span className="sm:hidden">{short}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Current: {settings.workTimings.workingDays?.length || 5} working days per week
                </p>
              </div>
            </div>
          )}

          {/* Shifts Tab */}
          {activeTab === 'shifts' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">Shift Management</h3>
                  <p className="text-sm text-gray-500">Create and manage shifts for different employee groups (morning, night, etc.)</p>
                </div>
                <Button onClick={() => openShiftModal()} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Add Shift
                </Button>
              </div>

              {shiftsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : shifts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-gray-600 font-medium mb-1">No Shifts Created</h4>
                  <p className="text-sm text-gray-400 mb-4">Default work timings from settings will be used for all employees.</p>
                  <Button onClick={() => openShiftModal()} variant="outline" className="inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Create First Shift
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {shifts.map((shift) => (
                    <div
                      key={shift._id}
                      className={`border rounded-xl p-5 transition-all ${
                        shift.isDefault ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 bg-white'
                      } ${!shift.isActive ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{shift.name}</h4>
                          {shift.isDefault && (
                            <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">Default</span>
                          )}
                          {shift.isNightShift && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">Night</span>
                          )}
                          {!shift.isActive && (
                            <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">Inactive</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openShiftModal(shift)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteShift(shift._id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Timing:</span>
                          <span className="ml-1 font-medium text-gray-900">{formatTime12h(shift.startTime)} – {formatTime12h(shift.endTime)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Min Hours:</span>
                          <span className="ml-1 font-medium text-gray-900">{shift.minimumWorkHours}h</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Grace:</span>
                          <span className="ml-1 font-medium text-gray-900">{shift.gracePeriodMinutes} min</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Employees:</span>
                          <span className="ml-1 font-medium text-gray-900">{shift.employeeCount || 0}</span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-1">
                        {shift.workingDays?.map(d => (
                          <span key={d} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {dayNames[d]}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Employees without an assigned shift will use the default Work Timings from the previous tab. 
                  Assign shifts to employees from the Employee Edit page.
                </p>
              </div>
            </div>
          )}

          {/* Payroll Settings Tab */}
          {activeTab === 'payroll' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Payroll Settings</h3>
                  <p className="text-sm text-gray-500">Configure salary calculation defaults for employees</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> These percentages will be used as default values when creating or editing employee salary structures. They will be locked and cannot be changed at employee level.
                </p>
              </div>

              {/* Pay Period Settings */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Pay Period Settings
                  {settings.payrollSettings?.payCycleLocked && (
                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Locked
                    </span>
                  )}
                </h4>
                <p className="text-sm text-gray-500 mb-4">Configure the payroll calculation period (e.g., 26th to 25th for monthly payroll)</p>
                
                {!settings.editPermissions?.canEditPayrollDates && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Restricted:</strong> Payroll date changes need super admin approval. Ask super admin to enable payroll date editing for this client before updating these fields.
                    </p>
                  </div>
                )}

                {settings.payrollSettings?.payCycleLocked && !settings.editPermissions?.canEditPayrollDates ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>⚠️ Locked:</strong> Pay period settings were locked on {settings.payrollSettings?.payCycleLockedAt ? new Date(settings.payrollSettings.payCycleLockedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'first save'}. These settings cannot be changed as payroll is generated based on this period every month.
                    </p>
                  </div>
                ) : settings.editPermissions?.canEditPayrollDates ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-green-800">
                      <strong>Approved:</strong> Super admin has allowed payroll date editing for this client. Changes here will be saved.
                    </p>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800">
                      <strong>⚠️ Important:</strong> Once you save pay period settings, they will be locked by default. Future changes will require super admin approval.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Period Start Day
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="28"
                        value={settings.payrollSettings?.payPeriodStartDay || 1}
                        onChange={(e) => setSettings({
                          ...settings,
                          payrollSettings: {
                            ...settings.payrollSettings,
                            payPeriodStartDay: Math.min(28, Math.max(1, parseInt(e.target.value) || 1))
                          }
                        })}
                        disabled={settings.payrollSettings?.payCycleLocked && !settings.editPermissions?.canEditPayrollDates}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          settings.payrollSettings?.payCycleLocked && !settings.editPermissions?.canEditPayrollDates
                            ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' 
                            : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Start day of month (e.g., 1 for 1st)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Period End Day
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="28"
                        value={settings.payrollSettings?.payPeriodEndDay || 1}
                        onChange={(e) => setSettings({
                          ...settings,
                          payrollSettings: {
                            ...settings.payrollSettings,
                            payPeriodEndDay: Math.min(28, Math.max(1, parseInt(e.target.value) || 1))
                          }
                        })}
                        disabled={settings.payrollSettings?.payCycleLocked && !settings.editPermissions?.canEditPayrollDates}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          settings.payrollSettings?.payCycleLocked && !settings.editPermissions?.canEditPayrollDates
                            ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed' 
                            : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">End day of next month (e.g., 1 for 1st of next month)</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Current Configuration:</strong> Payroll period runs from {settings.payrollSettings?.payPeriodStartDay || 1}th of current month to {settings.payrollSettings?.payPeriodEndDay || 1}th of next month (full calendar month)
                  </p>
                </div>
              </div>

              {/* Core Components */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Core Salary Components
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Basic Salary Percentage (% of CTC)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={settings.payrollSettings?.basicSalaryPercentage ?? 50}
                        onChange={(e) => setSettings({
                          ...settings,
                          payrollSettings: {
                            ...settings.payrollSettings,
                            basicSalaryPercentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                          }
                        })}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Percent className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Percentage of CTC that will be allocated as Basic Salary</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      HRA Percentage (% of CTC)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={settings.payrollSettings?.hraPercentage ?? 40}
                        onChange={(e) => setSettings({
                          ...settings,
                          payrollSettings: {
                            ...settings.payrollSettings,
                            hraPercentage: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))
                          }
                        })}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Percent className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Percentage of CTC that will be allocated as HRA</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Conveyance Allowance (% of CTC) — auto-calculated
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={Math.max(0, corePercentages.conveyance)}
                        readOnly
                        disabled
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Percent className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Remaining % = 100% − Basic % − HRA %</p>
                  </div>
                </div>
                <div className={`mt-4 p-3 rounded-lg text-sm ${corePercentages.isValid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {corePercentages.isValid ? (
                    <>Basic {corePercentages.basic}% + HRA {corePercentages.hra}% + Conveyance {corePercentages.conveyance}% = 100%</>
                  ) : (
                    <>Basic ({corePercentages.basic}%) + HRA ({corePercentages.hra}%) = {corePercentages.basic + corePercentages.hra}%, which exceeds 100%. Reduce Basic or HRA before saving.</>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  If an employee also has employer PF or other CTC-funded allowances, those amounts come out of the Conveyance share so the total stays at 100% of CTC.
                </p>
              </div>

              {/* Professional Tax */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Professional Tax
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      State-wise PT slabs applied to every payslip. Each employee's own state is used
                      when set; these are the company-wide defaults.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => updatePT({ enabled: !(ptSettings.enabled !== false) })}
                    className={`shrink-0 w-14 h-7 rounded-full transition-colors ${ptSettings.enabled !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                    aria-label="Toggle professional tax"
                  >
                    <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${ptSettings.enabled !== false ? 'translate-x-7' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                {ptSettings.enabled === false ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Professional tax is switched off for this company. No employee will be deducted PT,
                    even if it is enabled on their own profile.
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default PT State
                        </label>
                        <select
                          value={ptDefaultState}
                          onChange={(e) => updatePT({ defaultState: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">No default — set per employee</option>
                          <optgroup label="Levies professional tax">
                            {ptStateOptions.filter((o) => o.levied).map((o) => (
                              <option key={o.code} value={o.code}>
                                {o.name} ({FREQUENCY_LABELS[o.frequency]})
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="No professional tax levied">
                            {ptStateOptions.filter((o) => !o.levied).map((o) => (
                              <option key={o.code} value={o.code}>{o.name}</option>
                            ))}
                          </optgroup>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Used for employees who have no PT state on their own profile.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Apply To
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              className="mt-1"
                              checked={!ptSettings.overrideEmployeeState}
                              onChange={() => updatePT({ overrideEmployeeState: false })}
                            />
                            <span className="text-sm">
                              <span className="font-medium text-gray-900">Employee state first</span>
                              <span className="block text-xs text-gray-500">
                                Fall back to the default only when an employee has no state set.
                              </span>
                            </span>
                          </label>
                          <label className="flex items-start gap-3 px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              className="mt-1"
                              checked={!!ptSettings.overrideEmployeeState}
                              onChange={() => updatePT({ overrideEmployeeState: true })}
                            />
                            <span className="text-sm">
                              <span className="font-medium text-gray-900">Force default for everyone</span>
                              <span className="block text-xs text-gray-500">
                                Ignore per-employee states and use the default above.
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {ptStateConfig ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
                          <span className="text-sm font-medium text-gray-900">
                            {ptStateConfig.name} slabs
                          </span>
                          <span className="text-xs text-gray-500">
                            Filed {FREQUENCY_LABELS[ptStateConfig.frequency].toLowerCase()} · slab matched on {ptBasisLabel[ptStateConfig.salaryBasis]}
                          </span>
                        </div>

                        {(ptStateConfig.genderSlabs ? ['male', 'female'] : [undefined]).map((gender) => (
                          <div key={gender || 'all'} className="mb-3 last:mb-0">
                            {gender && (
                              <p className="text-xs font-medium text-gray-600 mb-1 capitalize">{gender}</p>
                            )}
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500">
                                    <th className="py-1 pr-4 font-medium">Salary</th>
                                    <th className="py-1 pr-4 font-medium">PT Amount</th>
                                    <th className="py-1 font-medium">Annual PT</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {getPTSlabs(ptDefaultState, gender).map(([from, to, amount, annual]) => (
                                    <tr key={`${from}-${to}`} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-4 text-gray-700">{formatSlabRange(from, to)}</td>
                                      <td className="py-1.5 pr-4 font-medium text-gray-900">{formatRupees(amount)}</td>
                                      <td className="py-1.5 text-gray-700">{formatRupees(annual)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}

                        <p className="text-xs text-gray-500 mt-3">
                          PT is deducted every month. For half-yearly and annual states the yearly
                          liability is spread across twelve months, and any remainder is settled in
                          {ptStateConfig.specialMonth === 2 ? ' February' : ' March'}.
                        </p>
                      </div>
                    ) : ptDefaultState ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        {ptStateOptions.find((o) => o.code === ptDefaultState)?.name} does not levy
                        professional tax — employees defaulting to it will have no PT deduction.
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Custom Salary Components */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Custom Salary Components
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      const newComponent = {
                        name: '',
                        type: 'earning',
                        calculationType: 'percentage_of_ctc',
                        value: 0,
                        isTaxable: true,
                        isActive: true,
                        order: (settings.payrollSettings?.salaryComponents?.length || 0) + 1
                      };
                      setSettings({
                        ...settings,
                        payrollSettings: {
                          ...settings.payrollSettings,
                          salaryComponents: [...(settings.payrollSettings?.salaryComponents || []), newComponent]
                        }
                      });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Component
                  </button>
                </div>

                {(!settings.payrollSettings?.salaryComponents || settings.payrollSettings.salaryComponents.length === 0) ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No custom components added yet</p>
                    <p className="text-sm">Add allowances, deductions, or other salary components</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {settings.payrollSettings.salaryComponents.map((component, index) => (
                      <div 
                        key={index} 
                        className={`p-4 rounded-lg border ${component.type === 'earning' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Component Name</label>
                            <input
                              type="text"
                              value={component.name}
                              onChange={(e) => {
                                const updated = [...settings.payrollSettings.salaryComponents];
                                updated[index].name = e.target.value;
                                setSettings({
                                  ...settings,
                                  payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                });
                              }}
                              placeholder="e.g., Conveyance Allowance"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                            <select
                              value={component.type}
                              onChange={(e) => {
                                const updated = [...settings.payrollSettings.salaryComponents];
                                updated[index].type = e.target.value;
                                setSettings({
                                  ...settings,
                                  payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="earning">Earning</option>
                              <option value="deduction">Deduction</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Calculation</label>
                            <select
                              value={component.calculationType}
                              onChange={(e) => {
                                const updated = [...settings.payrollSettings.salaryComponents];
                                updated[index].calculationType = e.target.value;
                                setSettings({
                                  ...settings,
                                  payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              <option value="percentage_of_ctc">% of CTC</option>
                              <option value="percentage_of_basic">% of Basic</option>
                              <option value="fixed">Fixed Amount</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {component.calculationType === 'fixed' ? 'Amount (₹)' : 'Percentage (%)'}
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={component.value}
                              onChange={(e) => {
                                const updated = [...settings.payrollSettings.salaryComponents];
                                updated[index].value = parseFloat(e.target.value) || 0;
                                setSettings({
                                  ...settings,
                                  payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                });
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={component.isTaxable}
                                onChange={(e) => {
                                  const updated = [...settings.payrollSettings.salaryComponents];
                                  updated[index].isTaxable = e.target.checked;
                                  setSettings({
                                    ...settings,
                                    payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                  });
                                }}
                                className="rounded border-gray-300"
                              />
                              Taxable
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                const updated = settings.payrollSettings.salaryComponents.filter((_, i) => i !== index);
                                setSettings({
                                  ...settings,
                                  payrollSettings: { ...settings.payrollSettings, salaryComponents: updated }
                                });
                              }}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payroll Signature */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Payroll Signature
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Upload an authorized signatory signature image (JPG/PNG, max 1MB). It will appear on the bottom-right of every generated payslip.
                </p>

                {payrollSignature?.url ? (
                  <div className="flex items-start gap-4">
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <img
                        src={payrollSignature.url}
                        alt="Payroll Signature"
                        className="h-16 max-w-[200px] object-contain"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-sm text-gray-600">
                        Uploaded: {payrollSignature.uploadedAt ? new Date(payrollSignature.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => signatureInputRef.current?.click()}
                          disabled={signatureLoading}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteSignature}
                          disabled={signatureLoading}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => signatureInputRef.current?.click()}
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
                  >
                    {signatureLoading ? (
                      <div className="flex items-center gap-2 text-indigo-600">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        Uploading...
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-sm font-medium text-gray-700">Click to upload signature</p>
                        <p className="text-xs text-gray-500 mt-1">JPG or PNG, max 1MB</p>
                      </>
                    )}
                  </div>
                )}
                <input
                  ref={signatureInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  className="hidden"
                  onChange={handleSignatureUpload}
                />
              </div>

              {/* Payroll Policies */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h4 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Payroll Policies
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  Configure automatic payroll calculation rules and policies.
                </p>
                
                <div className="space-y-4">
                  {/* Sandwich Rule */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Sandwich Rule</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        When enabled, if an employee takes leave on Friday and Monday, the weekend (Saturday & Sunday) will also be counted as leave days.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={settings.payrollSettings?.policies?.enableSandwichRule ?? false}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            payrollSettings: {
                              ...settings.payrollSettings,
                              policies: {
                                ...settings.payrollSettings?.policies,
                                enableSandwichRule: e.target.checked
                              }
                            }
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  {/* Late Mark Deduction */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Late Mark Deduction</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Automatically deduct salary for accumulated late marks (e.g., 3 late marks = 0.5 day deduction).
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={settings.payrollSettings?.policies?.enableLateMarkDeduction ?? true}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            payrollSettings: {
                              ...settings.payrollSettings,
                              policies: {
                                ...settings.payrollSettings?.policies,
                                enableLateMarkDeduction: e.target.checked
                              }
                            }
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  {/* Half Day Split */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Half Day Split</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Allow half-day attendance calculations when employees work partial days.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={settings.payrollSettings?.policies?.enableHalfDaySplit ?? true}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            payrollSettings: {
                              ...settings.payrollSettings,
                              policies: {
                                ...settings.payrollSettings?.policies,
                                enableHalfDaySplit: e.target.checked
                              }
                            }
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  {/* Holiday Work Compensation */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-900">Holiday Work Compensation</span>
                    <p className="text-sm text-gray-500 mt-1">
                      What employees receive when they work on a company holiday (from the Holidays calendar). A half day worked earns half.
                    </p>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { value: 'none', label: 'No extra compensation', hint: 'Paid like a normal day' },
                        { value: 'double_pay', label: 'Double Pay', hint: 'Extra pay in payroll' },
                        { value: 'comp_off', label: 'Compensatory Off', hint: 'Comp-off leave credited' }
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${holidayWork.compensation === opt.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'}`}
                        >
                          <input
                            type="radio"
                            name="holidayWorkCompensation"
                            value={opt.value}
                            checked={holidayWork.compensation === opt.value}
                            onChange={() => updateHolidayWork({ compensation: opt.value })}
                            className="mt-1 text-indigo-600"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                            <span className="block text-xs text-gray-500">{opt.hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    {holidayWork.compensation === 'double_pay' && (
                      <div className="mt-3 max-w-xs">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Pay rate for a worked holiday</label>
                        <select
                          value={holidayWork.payMultiplier ?? 2}
                          onChange={(e) => updateHolidayWork({ payMultiplier: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value={1.5}>1.5× per-day salary</option>
                          <option value={2}>2× per-day salary (double pay)</option>
                          <option value={2.5}>2.5× per-day salary</option>
                          <option value={3}>3× per-day salary</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          The holiday is already paid, so payroll adds the difference as "Holiday Work Pay". Per-day salary follows the Salary Division Method.
                        </p>
                      </div>
                    )}
                    {holidayWork.compensation === 'comp_off' && (
                      <div className="mt-3 max-w-xs">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Comp-off credited per holiday worked</label>
                        <select
                          value={holidayWork.compOffDays ?? 1}
                          onChange={(e) => updateHolidayWork({ compOffDays: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value={0.5}>0.5 day</option>
                          <option value={1}>1 day</option>
                          <option value={1.5}>1.5 days</option>
                          <option value={2}>2 days</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Credited to the employee's Compensatory Off balance when they punch out on the holiday.
                        </p>
                        {settings.leaveSettings?.leaveTypes?.compensatory?.enabled === false && (
                          <p className="text-xs text-red-600 mt-1">
                            The Compensatory Off leave type is disabled in Leave Management, so nothing will be credited until it is enabled.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Comp Off Adjustment */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Comp Off Adjustment</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Automatically adjust compensatory off against loss of pay in payroll calculations.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={settings.payrollSettings?.policies?.enableCompOffAdjustment ?? true}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            payrollSettings: {
                              ...settings.payrollSettings,
                              policies: {
                                ...settings.payrollSettings?.policies,
                                enableCompOffAdjustment: e.target.checked
                              }
                            }
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>

                  {/* Overtime */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">Overtime Calculation</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Include overtime pay calculations in payroll (1.5x multiplier for extra hours).
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={settings.payrollSettings?.policies?.enableOvertime ?? true}
                        onChange={(e) => {
                          setSettings({
                            ...settings,
                            payrollSettings: {
                              ...settings.payrollSettings,
                              policies: {
                                ...settings.payrollSettings?.policies,
                                enableOvertime: e.target.checked
                              }
                            }
                          });
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 rounded-lg p-4 mt-6">
                <h4 className="font-medium text-gray-900 mb-3">Salary Structure Preview (Example for ₹10,00,000 CTC)</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Annual CTC</p>
                    <p className="font-semibold text-gray-900">₹10,00,000</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Basic ({corePercentages.basic}%)</p>
                    <p className="font-semibold text-green-600">₹{(1000000 * corePercentages.basic / 100).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">HRA ({corePercentages.hra}% of CTC)</p>
                    <p className="font-semibold text-blue-600">₹{(1000000 * corePercentages.hra / 100).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Conveyance ({Math.max(0, corePercentages.conveyance)}% of CTC)</p>
                    <p className="font-semibold text-teal-600">₹{(1000000 * Math.max(0, corePercentages.conveyance) / 100).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Monthly Basic</p>
                    <p className="font-semibold text-gray-900">₹{((1000000 * corePercentages.basic / 100) / 12).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
                
                {/* Custom Components Preview */}
                {settings.payrollSettings?.salaryComponents?.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Custom Components (Annual)</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {settings.payrollSettings.salaryComponents.map((comp, idx) => {
                        const basicAmount = 1000000 * corePercentages.basic / 100;
                        let amount = 0;
                        if (comp.calculationType === 'percentage_of_ctc') {
                          amount = 1000000 * comp.value / 100;
                        } else if (comp.calculationType === 'percentage_of_basic') {
                          amount = basicAmount * comp.value / 100;
                        } else {
                          amount = comp.value * 12; // Fixed amount * 12 months
                        }
                        return (
                          <div key={idx} className={`p-3 rounded-lg border ${comp.type === 'earning' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                            <p className="text-gray-500 text-xs">{comp.name || 'Unnamed'} ({comp.value}{comp.calculationType === 'fixed' ? '₹/mo' : '%'})</p>
                            <p className={`font-semibold ${comp.type === 'earning' ? 'text-green-600' : 'text-red-600'}`}>
                              {comp.type === 'deduction' ? '-' : '+'}₹{Math.round(amount).toLocaleString('en-IN')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Leave Management Settings Tab */}
          {activeTab === 'leaves' && (
            <div className="space-y-6">
              {/* Leave Types Configuration */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Leave Types & Entitlements</h3>
                    <p className="text-sm text-gray-500">Configure annual leave entitlements for each leave type</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Sick Leave */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.leaveSettings?.leaveTypes?.sick?.enabled ?? true}
                            onChange={(e) => setSettings({
                              ...settings,
                              leaveSettings: {
                                ...settings.leaveSettings,
                                leaveTypes: {
                                  ...settings.leaveSettings?.leaveTypes,
                                  sick: { ...settings.leaveSettings?.leaveTypes?.sick, enabled: e.target.checked }
                                }
                              }
                            })}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="ml-2 font-medium text-red-900">Sick Leave</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-red-700 mb-1">Annual Days</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.sick?.annualEntitlement ?? 12}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                sick: { ...settings.leaveSettings?.leaveTypes?.sick, annualEntitlement: parseHalfDays(e.target.value) }
                              }
                            }
                          })}
                          min="0"
                          step="0.5"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-red-700 mb-1">Carry Forward</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.sick?.carryForward ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                sick: { ...settings.leaveSettings?.leaveTypes?.sick, carryForward: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-red-700 mb-1">Max Carry</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.sick?.maxCarryForward ?? 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                sick: { ...settings.leaveSettings?.leaveTypes?.sick, maxCarryForward: parseInt(e.target.value) || 0 }
                              }
                            }
                          })}
                          min="0"
                          disabled={!settings.leaveSettings?.leaveTypes?.sick?.carryForward}
                          className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-red-700 mb-1">Encashable</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.sick?.encashable ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                sick: { ...settings.leaveSettings?.leaveTypes?.sick, encashable: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Casual Leave */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.leaveSettings?.leaveTypes?.casual?.enabled ?? true}
                            onChange={(e) => setSettings({
                              ...settings,
                              leaveSettings: {
                                ...settings.leaveSettings,
                                leaveTypes: {
                                  ...settings.leaveSettings?.leaveTypes,
                                  casual: { ...settings.leaveSettings?.leaveTypes?.casual, enabled: e.target.checked }
                                }
                              }
                            })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 font-medium text-blue-900">Casual Leave</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-blue-700 mb-1">Annual Days</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.casual?.annualEntitlement ?? 12}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                casual: { ...settings.leaveSettings?.leaveTypes?.casual, annualEntitlement: parseHalfDays(e.target.value) }
                              }
                            }
                          })}
                          min="0"
                          step="0.5"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-blue-700 mb-1">Carry Forward</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.casual?.carryForward ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                casual: { ...settings.leaveSettings?.leaveTypes?.casual, carryForward: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-blue-700 mb-1">Max Carry</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.casual?.maxCarryForward ?? 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                casual: { ...settings.leaveSettings?.leaveTypes?.casual, maxCarryForward: parseInt(e.target.value) || 0 }
                              }
                            }
                          })}
                          min="0"
                          disabled={!settings.leaveSettings?.leaveTypes?.casual?.carryForward}
                          className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-blue-700 mb-1">Encashable</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.casual?.encashable ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                casual: { ...settings.leaveSettings?.leaveTypes?.casual, encashable: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Earned Leave */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.leaveSettings?.leaveTypes?.earned?.enabled ?? true}
                            onChange={(e) => setSettings({
                              ...settings,
                              leaveSettings: {
                                ...settings.leaveSettings,
                                leaveTypes: {
                                  ...settings.leaveSettings?.leaveTypes,
                                  earned: { ...settings.leaveSettings?.leaveTypes?.earned, enabled: e.target.checked }
                                }
                              }
                            })}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="ml-2 font-medium text-green-900">Earned Leave (Privilege Leave)</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-green-700 mb-1">Annual Days</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.earned?.annualEntitlement ?? 15}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                earned: { ...settings.leaveSettings?.leaveTypes?.earned, annualEntitlement: parseHalfDays(e.target.value) }
                              }
                            }
                          })}
                          min="0"
                          step="0.5"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-green-700 mb-1">Carry Forward</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.earned?.carryForward ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                earned: { ...settings.leaveSettings?.leaveTypes?.earned, carryForward: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-green-700 mb-1">Max Carry</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.earned?.maxCarryForward ?? 30}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                earned: { ...settings.leaveSettings?.leaveTypes?.earned, maxCarryForward: parseInt(e.target.value) || 0 }
                              }
                            }
                          })}
                          min="0"
                          disabled={!settings.leaveSettings?.leaveTypes?.earned?.carryForward}
                          className="w-full px-3 py-2 border rounded-lg text-sm disabled:bg-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-green-700 mb-1">Encashable</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.earned?.encashable ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                earned: { ...settings.leaveSettings?.leaveTypes?.earned, encashable: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Compensatory Leave */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.leaveSettings?.leaveTypes?.compensatory?.enabled ?? true}
                            onChange={(e) => setSettings({
                              ...settings,
                              leaveSettings: {
                                ...settings.leaveSettings,
                                leaveTypes: {
                                  ...settings.leaveSettings?.leaveTypes,
                                  compensatory: { ...settings.leaveSettings?.leaveTypes?.compensatory, enabled: e.target.checked }
                                }
                              }
                            })}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="ml-2 font-medium text-purple-900">Compensatory Off (Comp-Off)</span>
                        </label>
                      </div>
                    </div>
                    <p className="text-xs text-purple-600 mb-3">Earned by working on holidays/weekends. Max balance that can be accumulated.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Carry Forward</label>
                        <select
                          value={settings.leaveSettings?.leaveTypes?.compensatory?.carryForward ? 'yes' : 'no'}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                compensatory: { ...settings.leaveSettings?.leaveTypes?.compensatory, carryForward: e.target.value === 'yes' }
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-purple-700 mb-1">Max Accumulation</label>
                        <input
                          type="number"
                          value={settings.leaveSettings?.leaveTypes?.compensatory?.maxCarryForward ?? 10}
                          onChange={(e) => setSettings({
                            ...settings,
                            leaveSettings: {
                              ...settings.leaveSettings,
                              leaveTypes: {
                                ...settings.leaveSettings?.leaveTypes,
                                compensatory: { ...settings.leaveSettings?.leaveTypes?.compensatory, maxCarryForward: parseInt(e.target.value) || 0 }
                              }
                            }
                          })}
                          min="0"
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accrual Settings */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <RefreshCw className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Leave Accrual & Credit Rules</h3>
                    <p className="text-sm text-gray-500">Configure when and how leaves are credited to employees</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Frequency</label>
                    <select
                      value={settings.leaveSettings?.accrualMethod || 'monthly'}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          accrualMethod: e.target.value
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly (At start of year)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.leaveSettings?.accrualMethod === 'monthly' && 'Leaves credited on 1st of every month'}
                      {settings.leaveSettings?.accrualMethod === 'quarterly' && 'Leaves credited at start of each quarter (Jan, Apr, Jul, Oct)'}
                      {settings.leaveSettings?.accrualMethod === 'yearly' && 'Full year entitlement credited on 1st January'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Credit Day</label>
                    <select
                      value={settings.leaveSettings?.accrualDay || 1}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          accrualDay: parseInt(e.target.value)
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {[...Array(28)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Day of month/quarter when leaves are credited</p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.leaveSettings?.autoCredit?.enabled ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          leaveSettings: {
                            ...settings.leaveSettings,
                            autoCredit: { ...settings.leaveSettings?.autoCredit, enabled: e.target.checked }
                          }
                        })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Auto-credit leaves</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.leaveSettings?.autoCredit?.creditOnJoining ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          leaveSettings: {
                            ...settings.leaveSettings,
                            autoCredit: { ...settings.leaveSettings?.autoCredit, creditOnJoining: e.target.checked }
                          }
                        })}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Pro-rate for new joiners</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Carry Forward Settings */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Year-End Carry Forward</h3>
                    <p className="text-sm text-gray-500">Configure how unused leaves are handled at year end</p>
                  </div>
                </div>

                {/* Cross-Year Leave Toggle */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.leaveSettings?.allowCrossYearLeave ?? true}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          allowCrossYearLeave: e.target.checked
                        }
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900">Allow Cross-Year Leaves</span>
                      <p className="text-xs text-gray-500 mt-0.5">When enabled, employees can apply for leaves spanning December to January (across year boundaries). When disabled, they must apply separately for each year.</p>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="md:col-span-1">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.leaveSettings?.carryForward?.enabled ?? true}
                        onChange={(e) => setSettings({
                          ...settings,
                          leaveSettings: {
                            ...settings.leaveSettings,
                            carryForward: { ...settings.leaveSettings?.carryForward, enabled: e.target.checked }
                          }
                        })}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">Enable Carry Forward</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Days to Carry</label>
                    <input
                      type="number"
                      value={settings.leaveSettings?.carryForward?.maxDays ?? 30}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          carryForward: { ...settings.leaveSettings?.carryForward, maxDays: parseInt(e.target.value) || 0 }
                        }
                      })}
                      min="0"
                      disabled={!settings.leaveSettings?.carryForward?.enabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expiry After (Months)</label>
                    <input
                      type="number"
                      value={settings.leaveSettings?.carryForward?.expiryMonths ?? 3}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          carryForward: { ...settings.leaveSettings?.carryForward, expiryMonths: parseInt(e.target.value) || 0 }
                        }
                      })}
                      min="0"
                      disabled={!settings.leaveSettings?.carryForward?.enabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">0 = Never expires</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Process in Month</label>
                    <select
                      value={settings.leaveSettings?.carryForward?.processMonth || 4}
                      onChange={(e) => setSettings({
                        ...settings,
                        leaveSettings: {
                          ...settings.leaveSettings,
                          carryForward: { ...settings.leaveSettings?.carryForward, processMonth: parseInt(e.target.value) }
                        }
                      })}
                      disabled={!settings.leaveSettings?.carryForward?.enabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                    >
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April (Financial Year)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Summary Preview */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 p-6">
                <h4 className="font-medium text-gray-900 mb-4">Leave Policy Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Sick Leave</p>
                    <p className="font-semibold text-red-600">{effectiveEntitlement('sick')} days/year{settings.leaveSettings?.leaveTypes?.sick?.enabled === false ? ' (disabled)' : ''}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Casual Leave</p>
                    <p className="font-semibold text-blue-600">{effectiveEntitlement('casual')} days/year{settings.leaveSettings?.leaveTypes?.casual?.enabled === false ? ' (disabled)' : ''}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Earned Leave</p>
                    <p className="font-semibold text-green-600">{effectiveEntitlement('earned')} days/year{settings.leaveSettings?.leaveTypes?.earned?.enabled === false ? ' (disabled)' : ''}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Total Annual</p>
                    <p className="font-semibold text-indigo-600">
                      {effectiveEntitlement('sick') + effectiveEntitlement('casual') + effectiveEntitlement('earned')} days
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-gray-500">Credit Cycle</p>
                    <p className="font-semibold text-purple-600 capitalize">{settings.leaveSettings?.accrualMethod || 'Monthly'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          {activeTab !== 'office-locations' && activeTab !== 'departments' && (
            <div className="flex justify-end pt-6 mt-6 border-t border-gray-200">
              <Button type="submit" loading={saveLoading} className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Settings
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Add/Edit Office Location Modal */}
      <Modal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        title={editingLocation ? 'Edit Office Location' : 'Add Office Location'}
        size="md"
      >
        <div className="space-y-4">
          {locationError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
              {locationError}
            </div>
          )}
          
          {/* Info box - shows different message if editing */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-700">
              <strong>How to get coordinates:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Click "Get Current Location" if you're at the office</li>
                <li>Or open <button type="button" onClick={openGoogleMaps} className="text-blue-600 underline font-medium">Google Maps</button>, right-click on the office location, and copy the coordinates</li>
                <li>Or enter the latitude and longitude manually below</li>
              </ol>
            </div>
          </div>

          <Input
            label="Location Name *"
            value={locationForm.name}
            onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
            placeholder="e.g., Head Office, Branch 1"
          />
          <Input
            label="Address"
            value={locationForm.address}
            onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
            placeholder="Full address"
          />
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Latitude *"
              type="number"
              step="any"
              value={locationForm.latitude}
              onChange={(e) => setLocationForm({ ...locationForm, latitude: e.target.value })}
              placeholder="e.g., 17.385044"
            />
            <Input
              label="Longitude *"
              type="number"
              step="any"
              value={locationForm.longitude}
              onChange={(e) => setLocationForm({ ...locationForm, longitude: e.target.value })}
              placeholder="e.g., 78.486671"
            />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={getCurrentLocation}
            loading={gettingLocation}
            className="w-full flex items-center justify-center gap-2"
          >
            <Navigation className="w-4 h-4" />
            {gettingLocation ? 'Getting Location...' : 'Get Current Location'}
          </Button>

          {locationForm.latitude && locationForm.longitude && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">
                Location captured: {locationForm.latitude}, {locationForm.longitude}
              </span>
            </div>
          )}

          <Input
            label="Allowed Radius (meters)"
            type="number"
            value={locationForm.radius}
            onChange={(e) => setLocationForm({ ...locationForm, radius: e.target.value })}
            placeholder="200"
          />
          <p className="text-xs text-gray-500 -mt-2">
            Employees must be within this radius to punch in/out. Default: 200 meters
          </p>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsLocationModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveLocation}
              loading={locationLoading}
            >
              {editingLocation ? 'Update Location' : 'Add Location'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Shift Modal */}
      <Modal
        isOpen={isShiftModalOpen}
        onClose={() => setIsShiftModalOpen(false)}
        title={editingShift ? 'Edit Shift' : 'Create New Shift'}
      >
        <div className="space-y-4">
          <Input
            label="Shift Name"
            placeholder="e.g. Morning Shift, Night Shift"
            value={shiftForm.name}
            onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Time"
              type="time"
              value={shiftForm.startTime}
              onChange={(e) => setShiftForm({ ...shiftForm, startTime: e.target.value })}
            />
            <Input
              label="End Time"
              type="time"
              value={shiftForm.endTime}
              onChange={(e) => setShiftForm({ ...shiftForm, endTime: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Grace Period (minutes)"
              type="number"
              value={shiftForm.gracePeriodMinutes}
              onChange={(e) => setShiftForm({ ...shiftForm, gracePeriodMinutes: parseInt(e.target.value) || 0 })}
            />
            <Input
              label="Minimum Work Hours"
              type="number"
              value={shiftForm.minimumWorkHours}
              onChange={(e) => setShiftForm({ ...shiftForm, minimumWorkHours: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Working Days</label>
            <div className="flex flex-wrap gap-2">
              {[
                { day: 0, name: 'Sun' }, { day: 1, name: 'Mon' }, { day: 2, name: 'Tue' },
                { day: 3, name: 'Wed' }, { day: 4, name: 'Thu' }, { day: 5, name: 'Fri' }, { day: 6, name: 'Sat' },
              ].map(({ day, name }) => {
                const isSelected = shiftForm.workingDays?.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => {
                      const newDays = isSelected
                        ? shiftForm.workingDays.filter(d => d !== day)
                        : [...shiftForm.workingDays, day].sort((a, b) => a - b);
                      setShiftForm({ ...shiftForm, workingDays: newDays });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isSelected ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={shiftForm.isNightShift}
                onChange={(e) => setShiftForm({ ...shiftForm, isNightShift: e.target.checked })}
                className="w-4 h-4 text-indigo-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Night Shift</span>
              <span className="text-xs text-gray-400">(end time is next day)</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={shiftForm.isDefault}
                onChange={(e) => setShiftForm({ ...shiftForm, isDefault: e.target.checked })}
                className="w-4 h-4 text-indigo-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Default Shift</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsShiftModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveShift}
              loading={shiftsLoading}
            >
              {editingShift ? 'Update Shift' : 'Create Shift'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Department Modal */}
      <Modal
        isOpen={isDeptModalOpen}
        onClose={() => {
          setIsDeptModalOpen(false);
          setEditingDept(null);
          setDeptForm({ name: '', hod: '', canRaiseJobRequest: false, branchWise: false, escalateToHR: true, isHR: false });
        }}
        title={editingDept ? 'Edit Department' : 'Add Department'}
        size="md"
      >
        <div className="space-y-4">
          {/* Department Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={deptForm.name}
              onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter department name"
            />
          </div>

          {/* HOD Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Head of Department (HOD)
            </label>
            <select
              value={deptForm.hod || ''}
              onChange={(e) => setDeptForm({ ...deptForm, hod: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select HOD (Optional)</option>
              {employees
                .filter(emp => !editingDept || !deptForm.name || (emp.department || '').trim().toLowerCase() === deptForm.name.trim().toLowerCase())
                .map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name} {emp.employeeId ? `(${emp.employeeId})` : ''}
                  </option>
                ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {editingDept
                ? "Only employees currently in this department are listed. The HOD can raise job requests for this department (if enabled below)."
                : "The HOD can raise job requests for this department (if enabled below)"}
            </p>
          </div>

          {/* Can Raise Job Request Toggle */}
          <div className="bg-blue-50 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deptForm.canRaiseJobRequest}
                onChange={(e) => setDeptForm({ ...deptForm, canRaiseJobRequest: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded border-gray-300 mt-0.5"
              />
              <div>
                <span className="font-medium text-gray-900">Enable Job Request</span>
                <p className="text-sm text-gray-600 mt-0.5">
                  Allow the HOD of this department to create job requests for recruitment.
                  HR Head will review and approve these requests.
                </p>
              </div>
            </label>
          </div>

          {/* Approval Chain Engine settings */}
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Approval Chain</p>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deptForm.branchWise}
                onChange={(e) => setDeptForm({ ...deptForm, branchWise: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded border-gray-300 mt-0.5"
              />
              <div>
                <span className="font-medium text-gray-900">Branch-wise</span>
                <p className="text-sm text-gray-600 mt-0.5">
                  This department's designation ladder repeats once per branch. Leave requests, expenses, and
                  regularizations route to the approver in the employee's own branch.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deptForm.escalateToHR}
                onChange={(e) => setDeptForm({ ...deptForm, escalateToHR: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded border-gray-300 mt-0.5"
              />
              <div>
                <span className="font-medium text-gray-900">Escalate to HR when the chain runs out</span>
                <p className="text-sm text-gray-600 mt-0.5">
                  If there's no designation above an employee, or the top seat in this department is vacant,
                  route the request to HR's most senior employee instead of leaving it unassigned.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={deptForm.isHR}
                onChange={(e) => setDeptForm({ ...deptForm, isHR: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded border-gray-300 mt-0.5"
              />
              <div>
                <span className="font-medium text-gray-900">This is the HR department</span>
                <p className="text-sm text-gray-600 mt-0.5">
                  Marks this department as the fallback target when other departments' approval chains escalate
                  to HR. Only one department should have this enabled.
                </p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsDeptModalOpen(false);
                setEditingDept(null);
                setDeptForm({ name: '', hod: '', canRaiseJobRequest: false, branchWise: false, escalateToHR: true, isHR: false });
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveDepartment}
              loading={deptDesigLoading}
            >
              {editingDept ? 'Update Department' : 'Create Department'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminSettingsPage;
